import { useEffect, useState } from 'react';
import { Page } from '../components/Page.js';
import { Attachments } from '../components/Attachments.js';

type Mail = {
  uuid: string; source: string; from_addr: string | null; subject: string | null;
  sent_date: string | null; import_date: string; dkim_present: number; raw_sha256: string;
  linked_entity_type: string | null; linked_entity_id: string | null; ledger_seq: number;
};

const fmtDate = (s: string | null) => (s ? s.replace('T', ' ').replace(/\.\d+Z$/, '').slice(0, 19) : '—');

export function Documents() {
  const [mail, setMail] = useState<Mail[]>([]);
  const [open, setOpen] = useState<Record<string, { body: string | null; raw: string | null }>>({});
  const [reason, setReason] = useState('annual');
  const [withImages, setWithImages] = useState(false);
  const imgQ = withImages ? '&images=1' : '';

  async function load() {
    try { setMail((await fetch('/api/mail').then((r) => r.json())).mail ?? []); } catch { /* offline */ }
  }
  useEffect(() => { load(); }, []);

  async function reveal(uuid: string) {
    if (open[uuid]) { setOpen((o) => { const n = { ...o }; delete n[uuid]; return n; }); return; }
    const r = await fetch(`/api/mail/${uuid}/reveal`).then((x) => x.json());
    setOpen((o) => ({ ...o, [uuid]: r }));
  }

  return (
    <Page eyebrow="Records" title="Documents & correspondence"
      actions={<span className="pill pill-ok"><span className="dot dot-ok" />encrypted · anchored · dual-dated</span>}>

      <section className="card card-pad" style={{ marginBottom: 16 }}>
        <h3 style={{ marginTop: 0, fontSize: 15 }}>Full notification pack — bring to the police station</h3>
        <p className="muted" style={{ fontSize: 13 }}>
          A single PDF containing your <b>complete account, card, and identity numbers</b> (unmasked) so
          an officer can verify and record them. Generating this pack records a disclosure in your tamper-evident log.
        </p>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            value={reason}
            onChange={e => setReason(e.target.value)}
            style={{ fontSize: 13, padding: '4px 6px' }}
          >
            <option value="annual">Annual re-notification</option>
            <option value="new_bank">New bank / card / crypto account</option>
            <option value="change_address">Change of home address</option>
            <option value="new_passport">New passport / ID document</option>
            <option value="additional_address">Additional address (7-day stay)</option>
            <option value="change_name">Change of name / alias</option>
          </select>
          <a className="btn btn-primary" href={`/api/export/notification-pack.pdf?reason=${reason}${imgQ}`}>
            Download full notification pack (PDF)
          </a>
          <a className="btn btn-ghost" href={`/api/export/notification-pack.pdf?reason=${reason}&mode=update${imgQ}`}>
            Download update report (highlights new / changed)
          </a>
          <label className="muted" style={{ fontSize: 12.5, display: 'flex', gap: 6, alignItems: 'center' }}>
            <input type="checkbox" checked={withImages} onChange={(e) => setWithImages(e.target.checked)} style={{ width: 'auto', margin: 0 }} />
            Include device photos
          </label>
        </div>
        <p className="muted" style={{ fontSize: 12, marginTop: 8, marginBottom: 0 }}>
          The <b>update report</b> is the same pack with entries <span style={{ background: 'rgba(255,230,82,0.5)' }}>added</span> or
          {' '}<span style={{ background: 'rgba(255,168,77,0.5)' }}>changed</span> since your last in-person notification highlighted —
          handy for a routine visit where you only need to point out what’s new.
        </p>
      </section>

      <section className="cols2" style={{ marginBottom: 16 }}>
        <div className="card card-pad">
          <h3 style={{ marginTop: 0, fontSize: 15 }}>Hand to the police</h3>
          <p className="muted" style={{ fontSize: 13 }}>
            A station-ready <b>device notification</b> — a dated, complete list of your devices with a
            provenance footer tying it to the tamper-evident record. Print it or save it as a PDF.
          </p>
          <a className="btn btn-primary" href="/api/export/devices.pdf">Download device notification (PDF)</a>
        </div>
        <div className="card card-pad">
          <h3 style={{ marginTop: 0, fontSize: 15 }}>For a solicitor / court</h3>
          <p className="muted" style={{ fontSize: 13 }}>
            The self-verifying <b>proof packet</b> (zip) lets anyone confirm, with public tools, that your
            records existed by a Bitcoin-attested time and weren’t back-dated.
          </p>
          <a className="btn btn-ghost" href="/api/export/ppu">Download proof packet (.zip)</a>
        </div>
      </section>

      <section className="card card-pad" style={{ marginBottom: 16 }}>
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>
          <b>Imported correspondence</b> (e.g. PPU emails) is stored encrypted, hashed and anchored, with
          two dates: the email’s own sent date and when you imported it. The raw message is preserved so
          its DKIM signature can be verified later. To import, ask the operator to fetch and stage an email,
          then run <span className="mono">npm run import-mail --workspace @sor/server -- &lt;file.eml&gt;</span>
          with your passphrase.
        </p>
      </section>

      <div className="card">
        <div className="panel-head"><h3>Correspondence</h3><span className="hint">{mail.length}</span></div>
        <div className="ledger">
          {mail.length === 0 && <div style={{ padding: 16, color: 'var(--ink3)', fontSize: 13 }}>No correspondence imported yet.</div>}
          {mail.map((m) => {
            const o = open[m.uuid];
            return (
              <div key={m.uuid} style={{ borderBottom: '1px solid var(--border)' }}>
                <div className="ledger-row" style={{ borderBottom: 'none' }}>
                  <span className={`badge badge-${m.dkim_present ? 'ok' : 'mute'}`}>{m.dkim_present ? 'DKIM' : 'EMAIL'}</span>
                  <div className="ledger-main">
                    <div className="ledger-text"><strong>{m.subject ?? '(no subject)'}</strong> <span className="muted">· {m.from_addr ?? '?'}</span></div>
                    <div className="ledger-time">sent {fmtDate(m.sent_date)} · imported {fmtDate(m.import_date)} · seq {m.ledger_seq}{m.linked_entity_id ? ` · linked ${m.linked_entity_type}` : ''}</div>
                  </div>
                  <button className="btn btn-ghost" type="button" style={{ alignSelf: 'center' }} onClick={() => reveal(m.uuid)}>{o ? 'Hide' : 'Open'}</button>
                </div>
                {o && (
                  <div style={{ padding: 12, background: 'var(--surface2)', fontSize: 12.5, whiteSpace: 'pre-wrap', fontFamily: 'var(--mono)', maxHeight: 280, overflow: 'auto' }}>
                    {o.body || o.raw || '(empty)'}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="panel-foot mono">raw .eml preserved (encrypted) · sha256 commitment in the ledger</div>
      </div>

      <div className="card card-pad" style={{ marginTop: 16 }}>
        <h3 style={{ marginTop: 0, fontSize: 15 }}>Document library</h3>
        <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>
          Upload and store general documents (PDFs, images, letters) anchored to the tamper-evident ledger.
        </p>
        <Attachments entityType="document" />
      </div>
    </Page>
  );
}
